import { create } from 'zustand';
import { api } from '@/shared/api/client';
import { useAuthStore } from './authStore';

// Per-user choice of how the AI Companion is shown:
//   'floating' — a launcher bottom-right that opens a slide-over (default).
//   'docked'   — a persistent panel docked to the right; the page reserves
//                space for it via the --copilot-dock CSS var.
// Persisted to /user-preferences/copilot-layout (account-level, follows the
// user across devices); localStorage is a cold-boot cache.
export type CopilotLayout = 'floating' | 'docked';

const KEY = 'mrpcomp:copilot-layout';
const NAMESPACE = 'copilot-layout';

function loadCache(): CopilotLayout {
  try { return localStorage.getItem(KEY) === 'docked' ? 'docked' : 'floating'; } catch { return 'floating'; }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSave(layout: CopilotLayout) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    saveTimer = null;
    if (!useAuthStore.getState().user) return;
    try { await api.put(`/user-preferences/${NAMESPACE}`, { layout }); } catch { /* localStorage has it */ }
  }, 200);
}

type ServerRow = { value: { layout?: CopilotLayout } } | null;
type State = {
  layout: CopilotLayout;
  hydrated: boolean;
  setLayout: (l: CopilotLayout) => void;
  hydrate: () => Promise<void>;
};

export const useCopilotLayout = create<State>((set) => ({
  layout: loadCache(),
  hydrated: false,
  setLayout: (layout) => {
    set({ layout });
    try { localStorage.setItem(KEY, layout); } catch { /* quota */ }
    scheduleSave(layout);
  },
  hydrate: async () => {
    try {
      const row = await api.get<ServerRow>(`/user-preferences/${NAMESPACE}`);
      const l = row?.value?.layout;
      if (l === 'docked' || l === 'floating') {
        set({ layout: l, hydrated: true });
        try { localStorage.setItem(KEY, l); } catch { /* quota */ }
        return;
      }
    } catch { /* fall through to cache default */ }
    set({ hydrated: true });
  },
}));
